import {useCallback, useEffect, useRef, useState} from 'react'

// Fallback only — the overlay normally unmounts the moment MUI reports its exit
// (see `onTransitionExited` below). This is the ceiling for how long a lost
// report can keep a closed overlay in the DOM: longer than the slowest exit we
// render, which is Grow with the default `timeout: 'auto'` (Popover, Menu) at
// roughly 450ms for a tall paper. Fade (Dialog) and Slide (Drawer) are ~225ms.
export const OVERLAY_EXIT_MS = 600

type OverlayMount = {
  // Render the overlay while this is true.
  mounted: boolean
  // Pass to the overlay as `onTransitionExited` (Popover, Menu, Dialog and
  // Drawer all forward it to their Modal). Unmounts immediately, so a healthy
  // close behaves exactly as it does without this hook.
  onTransitionExited: () => void
}

// Bounds how long a MUI overlay (Popover / Menu / Dialog / Drawer) can stay
// mounted after it closes.
//
// MUI only stops rendering a Modal-based overlay once its exit transition
// reports back: `Modal` returns null for `!keepMounted && !open && exited`, and
// its `visibility: hidden` style needs that same `exited`. When the report is
// lost, the Modal root stays in the DOM at `position: fixed; inset: 0;
// z-index: 1300` with an invisible backdrop — a page that looks completely
// normal and answers no clicks, until a reload. (A Dialog also keeps the body
// scroll lock, because it passes `closeAfterTransition`.)
//
// That report does get lost. Grow's `timeout: 'auto'` hands
// react-transition-group `timeout: null`, so there is no fallback timer, and a
// re-render landing inside the exit window can invalidate the pending end
// callback. Measured three times in this repo before this hook existed: the
// stuck Add Widget menu portal (AB#5750), the stuck rule-runs modal (AB#5784),
// and the placement dialog that lingers "at opacity 1, on an idle page" in
// signalsWorkspacePlacement.cy.ts. As AB#5784 found, a numeric
// `transitionDuration` does not save you — the cancelled callback is the
// transition's, not React's.
//
// The Signals Workspace hits it hardest because its whole page state lives in
// URL search params: closing a popover there usually also writes
// `setSearchParams`, and that router navigation is the re-render in question.
//
// So: keep MUI's own report as the fast path, and own a timer it cannot cancel
// as the floor. MUI restores the scroll lock and the siblings' `aria-hidden` in
// the Modal's unmount cleanup, so dropping the node early is safe.
export const useOverlayMount = (open: boolean, exitMs: number = OVERLAY_EXIT_MS): OverlayMount => {
  const [mounted, setMounted] = useState(open)
  const openRef = useRef(open)
  openRef.current = open

  useEffect(() => {
    if (open) {
      setMounted(true)
      return undefined
    }
    if (!mounted) {
      return undefined
    }
    const timer = window.setTimeout(() => setMounted(false), exitMs)
    return () => window.clearTimeout(timer)
  }, [open, mounted, exitMs])

  const onTransitionExited = useCallback(() => {
    // A late report from an exit that was interrupted by a re-open must not
    // drop the overlay that is now on screen.
    if (!openRef.current) {
      setMounted(false)
    }
  }, [])

  // `open` wins immediately, so re-opening inside the grace window never waits
  // for a state update.
  return {mounted: open || mounted, onTransitionExited}
}
